import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTeam } from '../context/TeamContext';
import { useAuth } from '../context/AuthContext';
import {
  Calendar, Users, ClipboardList, FileText, ArrowRight, Plus,
  Clock, Target, Video, UserCheck, AlertTriangle, Shield,
  ChevronRight, BarChart3, Dumbbell, Heart, Trophy,
  TrendingUp, Activity
} from 'lucide-react';
import { cn, formatearFecha } from '../lib/utils';
import RemindersPanel from '../components/RemindersPanel';

import { API_URL } from '../lib/apiUrl';

const StatCard = ({ label, value, icon: Icon, color, bgColor, path, loading }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(path)}
      className="stat-card text-left w-full group active:scale-[0.96] transition-transform"
      data-testid={`dashboard-kpi-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="accent-bar" style={{ background: color }} />
      <div className="flex items-start justify-between mb-3">
        <p className="section-label" style={{ color: 'var(--text-2)' }}>{label}</p>
        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: bgColor }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
      </div>
      {loading ? (
        <div className="skeleton h-10 w-16 rounded" />
      ) : (
        // El número va en el color del texto normal: cuando iba del color del
        // acento, en el tema claro el ámbar quedaba a 1,43 de contraste y el cian
        // a 1,70 (lo mínimo legible es 4,5), o sea prácticamente invisible.
        <p className="metric-number text-5xl" style={{ color: 'var(--text-0)' }}>{value}</p>
      )}
      <div className="flex items-center gap-1 mt-3"
        style={{ color: 'var(--text-2)', transition: 'color 200ms' }}
        onMouseEnter={e => e.currentTarget.style.color = 'var(--text-0)'}
        onMouseLeave={e => e.currentTarget.style.color = 'var(--text-2)'}
      >
        <span className="text-xs">Ver detalles</span>
        <ChevronRight className="w-3 h-3" />
      </div>
    </button>
  );
};


export default function DashboardPage() {
  const navigate = useNavigate();
  const { currentTeam, teams } = useTeam();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/stats?team_id=${currentTeam.id}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, [currentTeam]);

  useEffect(() => {
    if (currentTeam) fetchStats();
    else setLoading(false);
  }, [currentTeam, fetchStats]);

  // No team state
  if (!currentTeam && !loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center animate-fadeIn">
        <div
          className="max-w-sm w-full p-8 rounded-2xl text-center"
          style={{ background: 'var(--surface-0)', border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-lg)' }}
        >
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
            style={{ background: 'var(--primary-soft)', border: '1px solid var(--primary-border)' }}
          >
            <Shield className="w-8 h-8" style={{ color: 'var(--primary)' }} />
          </div>
          <h2 className="text-3xl font-black mb-2" style={{ fontFamily: 'Inter, sans-serif', color: 'var(--text-0)' }}>
            BIENVENIDO
          </h2>
          <p className="text-sm mb-6" style={{ color: 'var(--text-2)' }}>
            Crea tu primer equipo para empezar a planificar entrenamientos profesionales.
          </p>
          <button
            onClick={() => navigate('/equipos/nuevo')}
            className="w-full py-2.5 rounded-xl font-bold text-sm btn-glow flex items-center justify-center gap-2"
            style={{ background: 'var(--primary)', color: 'var(--primary-ink)' }}
            data-testid="create-first-team-btn"
          >
            <Plus className="w-4 h-4" />
            Crear primer equipo
          </button>
        </div>
      </div>
    );
  }

  // Los acentos van por variable del tema, no en hexadecimal: los hexadecimales
  // estaban pensados para el fondo oscuro y sobre blanco no se leían.
  const kpiCards = [
    { label: 'Jugadores', value: stats?.players ?? 0, icon: Users, color: 'var(--info)', bgColor: 'var(--surface-2)', path: '/squad' },
    { label: 'Sesiones', value: stats?.sessions ?? 0, icon: ClipboardList, color: 'var(--primary)', bgColor: 'var(--surface-2)', path: '/sesiones' },
    { label: 'Partidos', value: stats?.matches ?? 0, icon: Trophy, color: 'var(--warning)', bgColor: 'var(--surface-2)', path: '/partidos' },
    { label: 'Alertas Wellness', value: stats?.wellness_alerts ?? 0, icon: Heart, color: 'var(--danger)', bgColor: 'var(--surface-2)', path: '/wellness' },
  ];

  // Los seis atajos llevaban seis colores distintos (y el de "Nueva sesión" era
  // 'var(--primary)', que al concatenarle el alfa daba 'var(--primary)15': CSS
  // inválido, así que ese fondo NO se pintaba). Ahora todos en el verde de
  // MiCoach: son atajos, no información que haya que distinguir por color.
  const quickActions = [
    { label: 'Nueva sesión', icon: ClipboardList, path: '/sesiones/nueva' },
    { label: 'Microciclo', icon: Calendar, path: '/microciclo' },
    { label: 'Gimnasio', icon: Dumbbell, path: '/gimnasio' },
    { label: 'Asistencia', icon: UserCheck, path: '/asistencia' },
    { label: 'Wellness', icon: Heart, path: '/wellness' },
    { label: 'Informes', icon: FileText, path: '/informes' },
  ];

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="dashboard-page">

      {/* Header — desktop: título grande + CTAs; móvil: solo CTAs */}
      <div className="flex flex-col gap-3">
        {/* Título + badges: solo desktop */}
        <div className="hidden md:flex flex-row items-end justify-between gap-4">
          <div>
            <p className="section-label mb-1" style={{ color: 'var(--primary)' }}>Panel de Control</p>
            <h1 className="text-display" style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', color: 'var(--text-0)' }}>
              {currentTeam?.name?.toUpperCase()}
            </h1>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {currentTeam?.category_age && <span className="badge badge-neutral">{currentTeam.category_age}</span>}
              {currentTeam?.level && <span className="badge badge-neutral capitalize">{currentTeam.level}</span>}
              {currentTeam?.style_tags?.slice(0, 2).map(tag => (
                <span key={tag} className="badge badge-primary">{tag}</span>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/sesiones/nueva')}
              className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-semibold active:scale-95 transition-transform"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-strong)', color: 'var(--text-1)' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
            >
              <Plus className="w-4 h-4" />Sesión
            </button>
            <button
              onClick={() => navigate('/microciclo')}
              className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-bold btn-glow active:scale-95 transition-transform"
              style={{ background: 'var(--primary)', color: 'var(--primary-ink)' }}
            >
              <Calendar className="w-4 h-4" />Planificar
            </button>
          </div>
        </div>

        {/* CTAs full-width: solo móvil */}
        <div className="flex gap-2 md:hidden">
          <button
            onClick={() => navigate('/sesiones/nueva')}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-semibold active:scale-95 transition-transform"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border-strong)', color: 'var(--text-1)' }}
          >
            <Plus className="w-4 h-4" />Nueva sesión
          </button>
          <button
            onClick={() => navigate('/microciclo')}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-bold btn-glow active:scale-95 transition-transform"
            style={{ background: 'var(--primary)', color: 'var(--primary-ink)' }}
          >
            <Calendar className="w-4 h-4" />Planificar
          </button>
        </div>

        {/* Wellness alert — solo móvil, solo si hay alertas */}
        {!loading && stats?.wellness_alerts > 0 && (
          <button
            onClick={() => navigate('/wellness')}
            className="flex md:hidden items-center gap-3 p-3.5 rounded-2xl w-full active:scale-[0.98] transition-transform"
            style={{ background: 'rgba(255,77,109,0.10)', border: '1px solid rgba(255,77,109,0.28)' }}
          >
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,77,109,0.18)' }}>
              <Heart className="w-4 h-4" style={{ color: 'var(--danger)' }} />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-bold" style={{ color: 'var(--danger)' }}>
                {stats.wellness_alerts} alerta{stats.wellness_alerts !== 1 ? 's' : ''} de wellness
              </p>
              <p className="text-[10px]" style={{ color: 'var(--text-2)' }}>Toca para revisar el estado del equipo</p>
            </div>
            <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--danger)' }} />
          </button>
        )}
      </div>

      {/* Avisos accionables (partido próximo, convocatoria pendiente, sesión de hoy…) */}
      <RemindersPanel />

      {/* KPI Cards — grid responsive unificado, mismo StatCard en todas las pantallas */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 stagger">
        {kpiCards.map(card => (
          <StatCard key={card.label} {...card} loading={loading} />
        ))}
      </div>

      {/* Quick Actions */}
      <div>
        <p className="section-label mb-3">Acciones rápidas</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {quickActions.map(action => (
            <button
              key={action.label}
              onClick={() => navigate(action.path)}
              className="flex items-center gap-2.5 p-3 rounded-xl text-left active:scale-[0.96] transition-transform"
              style={{ background: 'var(--surface-0)', border: '1px solid var(--border-subtle)' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.backgroundColor = 'var(--surface-1)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; e.currentTarget.style.backgroundColor = 'var(--surface-0)'; }}
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--primary-soft)', border: '1px solid var(--primary-border)' }}>
                <action.icon className="w-4 h-4" style={{ color: 'var(--primary)' }} />
              </div>
              {/* Sin `truncate`: en 6 columnas el hueco es de 62 px y "Nueva
                  sesión" necesita 78, así que salía cortada. Ahora pasa a dos
                  líneas y la rejilla iguala la altura de los botones. */}
              <span className="text-xs font-semibold leading-tight" style={{ color: 'var(--text-1)' }}>{action.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Recent Sessions */}
        <div
          className="rounded-2xl p-4 md:p-5"
          style={{ background: 'var(--surface-0)', border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-soft)' }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ background: 'rgba(var(--primary-rgb),0.10)', border: '1px solid rgba(var(--primary-rgb),0.20)' }}>
                <ClipboardList className="w-3.5 h-3.5" style={{ color: 'var(--primary)' }} />
              </div>
              <h3 className="text-sm font-bold" style={{ color: 'var(--text-0)' }}>Sesiones recientes</h3>
            </div>
            <button
              onClick={() => navigate('/sesiones')}
              className="flex items-center gap-1 text-xs"
              style={{ color: 'var(--text-2)', transition: 'color 200ms' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--primary)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-2)'}
            >
              Ver todas <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          {loading ? (
            <div className="space-y-2">
              {[1,2,3].map(i => <div key={i} className="skeleton h-12 rounded-xl" />)}
            </div>
          ) : stats?.recent_sessions?.length > 0 ? (
            <div className="space-y-1">
              {stats.recent_sessions.slice(0, 3).map(session => (
                <button key={session.id}
                  onClick={() => navigate(`/sesiones/${session.id}`)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left group active:scale-[0.98] transition-transform"
                  style={{ transition: 'background-color 200ms, transform 100ms' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.035)'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-black"
                    style={{ background: 'rgba(var(--primary-rgb),0.10)', color: 'var(--primary)', fontFamily: 'Inter, sans-serif' }}
                  >
                    {formatearFecha(session.date, 'dd')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold truncate" style={{ color: 'var(--text-0)' }}>
                      {formatearFecha(session.date, 'EEEE d MMM')}
                    </p>
                    <p className="text-xs truncate" style={{ color: 'var(--text-2)' }}>
                      {session.intensity} • {session.total_time} min
                    </p>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100" style={{ color: 'var(--text-2)', transition: 'opacity 200ms' }} />
                </button>
              ))}
            </div>
          ) : (
            <div className="empty-state py-10">
              <p className="text-xs" style={{ color: 'var(--text-2)' }}>No hay sesiones aún</p>
              <button
                onClick={() => navigate('/sesiones/nueva')}
                className="text-xs mt-2"
                style={{ color: 'var(--primary)', transition: 'opacity 200ms' }}
              >Crear primera sesión</button>
            </div>
          )}
        </div>

        {/* Recent Matches */}
        <div
          className="rounded-2xl p-4 md:p-5"
          style={{ background: 'var(--surface-0)', border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-soft)' }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ background: 'rgba(255,211,77,0.10)', border: '1px solid rgba(255,211,77,0.20)' }}>
                <Trophy className="w-3.5 h-3.5" style={{ color: 'var(--warning)' }} />
              </div>
              <h3 className="text-sm font-bold" style={{ color: 'var(--text-0)' }}>Partidos recientes</h3>
            </div>
            <button
              onClick={() => navigate('/partidos')}
              className="flex items-center gap-1 text-xs"
              style={{ color: 'var(--text-2)', transition: 'color 200ms' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--warning)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-2)'}
            >
              Ver todos <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          {loading ? (
            <div className="space-y-2">
              {[1,2,3].map(i => <div key={i} className="skeleton h-12 rounded-xl" />)}
            </div>
          ) : stats?.recent_matches?.length > 0 ? (
            <div className="space-y-1">
              {stats.recent_matches.map(match => (
                <button key={match.id}
                  onClick={() => navigate(`/partidos/${match.id}/plan`)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left group active:scale-[0.98] transition-transform"
                  style={{ transition: 'background-color 200ms, transform 100ms' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.035)'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-black"
                    style={{ background: 'rgba(255,211,77,0.10)', color: 'var(--warning)', fontFamily: 'Inter, sans-serif' }}
                  >
                    {match.home_away === 'home' ? 'H' : 'A'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold truncate" style={{ color: 'var(--text-0)' }}>vs {match.opponent}</p>
                    <p className="text-xs" style={{ color: 'var(--text-2)' }}>
                      {formatearFecha(match.date, 'dd MMM')} • {match.home_away === 'home' ? 'Local' : 'Visitante'}
                    </p>
                  </div>
                  {match.result && (
                    <span
                      className="text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                      style={{
                        background: match.result.startsWith('W') ? 'rgba(var(--primary-rgb),0.12)' : match.result.startsWith('D') ? 'rgba(255,211,77,0.12)' : 'rgba(255,77,109,0.12)',
                        color: match.result.startsWith('W') ? 'var(--primary)' : match.result.startsWith('D') ? '#ffd34d' : '#ff4d6d',
                      }}
                    >{match.result}</span>
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="empty-state py-10">
              <p className="text-xs" style={{ color: 'var(--text-2)' }}>No hay partidos aún</p>
              <button onClick={() => navigate('/partidos')} className="text-xs mt-2" style={{ color: 'var(--warning)' }}>
                Registrar partido
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
